import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCustomer } from "../contexts/CustomerContext";
import { ROUTES } from "../constants/routes";
import { LoadingState } from "../components/composite/LoadingState/LoadingState";

/**
 * CustomerSelectionGuard
 * Ensures users have selected a customer before accessing protected routes
 *
 * Flow:
 * 1. Simple users (role: 'user') → Auto-select primary customer → Dashboard
 * 2. Consultants/Admins without selected customer → Redirect to selection page
 * 3. Consultants/Admins with selected customer → Allow access
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 */
export default function CustomerSelectionGuard({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const {
    selectedCustomer,
    getPrimaryCustomer,
    selectCustomer,
    accessibleCustomers,
    loading: customerLoading,
  } = useCustomer();

  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkCustomerSelection = async () => {
      // Wait for auth to be ready
      if (!isAuthenticated || !user) {
        setIsChecking(false);
        return;
      }

      // Wait for customer context to load
      if (customerLoading) {
        return;
      }

      // Skip customer selection page itself
      if (location.pathname === ROUTES.SELECT_CUSTOMER) {
        setIsChecking(false);
        return;
      }

      try {
        // Simple user flow: Auto-select primary customer
        if (user.role === "user") {
          if (!selectedCustomer) {
            const primaryCustomer = await getPrimaryCustomer();

            if (primaryCustomer) {
              selectCustomer(primaryCustomer);
            } else {
              console.warn("No primary customer found for simple user");
            }
          }
          setIsChecking(false);
          return;
        }

        // Consultant/Admin flow: Require customer selection
        if (user.role === "consultant" || user.role === "admin") {
          // If no customer selected, redirect to selection page
          if (!selectedCustomer) {
            // Check if there are accessible customers
            if (accessibleCustomers && accessibleCustomers.length > 0) {
              navigate(ROUTES.SELECT_CUSTOMER, { replace: true });
            } else {
              console.warn("No accessible customers available");
            }
          }
          setIsChecking(false);
          return;
        }

        setIsChecking(false);
      } catch (error) {
        console.error("Error in customer selection guard:", error);
        setIsChecking(false);
      }
    };

    checkCustomerSelection();
  }, [
    isAuthenticated,
    user,
    selectedCustomer,
    accessibleCustomers,
    customerLoading,
    location.pathname,
    navigate,
    getPrimaryCustomer,
    selectCustomer,
  ]);

  // Show loading while checking
  if (isChecking || customerLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingState text="Loading customer context..." variant="spinner" />
      </div>
    );
  }

  // Render children if all checks pass
  return <>{children}</>;
}
